import type { Task } from "@/schema"
import { InfiniteData, QueryKey, useQueryClient } from "@tanstack/react-query"

import type { GroupedTasksResponse } from "./api"

function applyOptimisticTaskUpdate(task: Task, update: Partial<Task>): Task {
  const merged = { ...task, ...update }
  // React Query's structural sharing recurses into a changed Date (Date -> Date) and,
  // since a Date has no enumerable own keys, collapses it to `{}` — which then crashes
  // date formatting on the next render. Persist deadline_at as an ISO string (the
  // server's wire shape) so the optimistic cache stays a consistently parseable value.
  if (merged.deadline_at instanceof Date) {
    merged.deadline_at = merged.deadline_at.toISOString() as unknown as Date
  }
  return merged
}

export function useOptimisticTaskUpdate() {
  const queryClient = useQueryClient()

  return async (newTask: Partial<Task> & { id: string }, { targetQueryKeys }: { targetQueryKeys: QueryKey[] }) => {
    await Promise.all(targetQueryKeys.map((queryKey) => queryClient.cancelQueries({ queryKey })))

    await queryClient.cancelQueries({ queryKey: ["tasks", "grouped"] })

    const previousData: Record<string, unknown> = {}

    targetQueryKeys.forEach((queryKey) => {
      const serializedKey = JSON.stringify(queryKey)
      previousData[serializedKey] = queryClient.getQueryData(queryKey)
    })

    const queryCache = queryClient.getQueryCache()
    queryCache.getAll().forEach((query) => {
      const queryKey = query.queryKey as QueryKey
      if (queryKey[0] === "tasks" && queryKey[1] === "grouped") {
        const serializedKey = JSON.stringify(queryKey)
        previousData[serializedKey] = query.state.data
      }
    })

    const individualQueryKey = ["task", newTask.id]
    queryClient.setQueryData<Task>(individualQueryKey, (old) => {
      if (!old) return undefined
      return applyOptimisticTaskUpdate(old, newTask)
    })

    targetQueryKeys.forEach((queryKey) => {
      if (queryKey[0] === "task") return

      if (queryKey[0] === "tasks" && queryKey[1] === "grouped") {
        queryClient.setQueryData<InfiniteData<GroupedTasksResponse>>(queryKey, (old) => {
          if (!old) return old

          return {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              groups: Object.entries(page.groups).reduce(
                (acc, [groupKey, groupData]) => {
                  acc[groupKey] = {
                    ...groupData,
                    tasks: groupData.tasks.map((task) =>
                      task.id === newTask.id ? applyOptimisticTaskUpdate(task, newTask) : task
                    ),
                  }
                  return acc
                },
                {} as typeof page.groups
              ),
            })),
          }
        })
        return
      }

      queryClient.setQueryData<{ tasks: Task[] }>(queryKey, (old) => {
        if (!old?.tasks) return old
        const updatedTasks = old.tasks.map((task) =>
          task.id === newTask.id ? applyOptimisticTaskUpdate(task, newTask) : task
        )
        return { ...old, tasks: updatedTasks }
      })
    })

    // Optimistically update dashboard task queries (Task[] format)
    queryCache.getAll().forEach((query) => {
      const queryKey = query.queryKey as QueryKey
      if (Array.isArray(queryKey) && queryKey[0] === "tasks" && queryKey[1] === "dashboard") {
        const serializedKey = JSON.stringify(queryKey)
        if (!previousData[serializedKey]) {
          previousData[serializedKey] = query.state.data
        }
        queryClient.setQueryData<Task[]>(queryKey, (old) => {
          if (!old) return old
          return old.map((task) => (task.id === newTask.id ? applyOptimisticTaskUpdate(task, newTask) : task))
        })
      }
    })

    queryCache.getAll().forEach((query) => {
      const queryKey = query.queryKey as QueryKey
      if (
        queryKey[0] === "tasks" &&
        queryKey[1] === "grouped" &&
        !targetQueryKeys.some((tk) => JSON.stringify(tk) === JSON.stringify(queryKey))
      ) {
        queryClient.setQueryData<InfiniteData<GroupedTasksResponse>>(queryKey, (old) => {
          if (!old) return old

          return {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              groups: Object.entries(page.groups).reduce(
                (acc, [groupKey, groupData]) => {
                  acc[groupKey] = {
                    ...groupData,
                    tasks: groupData.tasks.map((task) =>
                      task.id === newTask.id ? applyOptimisticTaskUpdate(task, newTask) : task
                    ),
                  }
                  return acc
                },
                {} as typeof page.groups
              ),
            })),
          }
        })
      }
    })

    return previousData
  }
}
